/**
 * useEditDetailLoader — never open an edit form on a list row.
 *
 * ME3a made the seven plural link sets AUTHORITATIVE: whatever the form
 * submits is the complete manual set for that type, and the backend
 * deletes every manual link the payload omits (plus the derived links
 * those fed — ``multi_entity_link_service.py:443-452, 496-524``).
 *
 * That contract is correct, and it is safe exactly as long as the form
 * starts from a row that HAS the link arrays. A list row does not:
 * ``TaskListItem`` and ``WorkReportListItem`` carry only the legacy
 * singular ``*_id`` fields, so ``manualIdsFor`` falls back to at most ONE
 * id per type — and an untouched save then writes that reduced set back,
 * silently destroying the rest. Only ``TaskRead`` and
 * ``WorkReportDetailRead`` carry the arrays.
 *
 * So the fix is not to weaken the backend and not to fatten the list
 * schemas: it is to load the DETAIL row before the modal opens. This
 * hook is that step, in one place rather than five, because the three
 * things around it are easy to get subtly wrong:
 *
 *   - **Nothing opens early.** The modal is opened by ``onLoaded``, which
 *     runs only after the detail row is in hand. A failed fetch shows a
 *     toast and leaves the page exactly as it was — no empty form, no
 *     half-filled one, and above all no mutation.
 *   - **A second click cannot start a second load** for the same row,
 *     and callers get ``pendingId`` to disable the control meanwhile.
 *   - **A slow answer cannot overwrite a newer one.** The monotonic
 *     sequence guard is the same one ``useAsyncPagedSearch`` uses; two
 *     quick clicks on different rows can only ever open the row that was
 *     clicked last.
 *
 * The callbacks are held in refs so ``requestEdit`` keeps a stable
 * identity across renders. That is deliberate: an inline callback in a
 * dependency array is precisely what makes the pickers re-fetch page 1
 * on every keystroke, and this hook must not repeat it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/** ``row.id`` or a bare id, as a string; "" when neither is usable. */
function idOf(rowOrId) {
  if (typeof rowOrId === "string" || typeof rowOrId === "number") {
    return String(rowOrId);
  }
  return rowOrId?.id ? String(rowOrId.id) : "";
}

export default function useEditDetailLoader({
  fetchDetail,
  onLoaded,
  errorMessage,
}) {
  // The id whose detail row is in flight, or null. Callers disable the
  // row's edit control while it matches.
  const [pendingId, setPendingId] = useState(null);
  // Mirrors pendingId for the synchronous double-click check — state is
  // not readable again until the next render, a click is faster.
  const pendingRef = useRef(null);
  const seqRef = useRef(0);

  // "Latest ref" for the caller's callbacks: read at call time, never a
  // dependency, so requestEdit never changes identity.
  const onLoadedRef = useRef(onLoaded);
  const errorMessageRef = useRef(errorMessage);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
    errorMessageRef.current = errorMessage;
  });

  const settle = useCallback((seq) => {
    if (seq !== seqRef.current) return false; // a newer click already won
    pendingRef.current = null;
    setPendingId(null);
    return true;
  }, []);

  const requestEdit = useCallback(
    async (rowOrId) => {
      const id = idOf(rowOrId);
      if (!id) return;
      // A second click on the SAME row while its detail is loading is a
      // no-op rather than a second request.
      if (pendingRef.current === id) return;

      const seq = ++seqRef.current;
      pendingRef.current = id;
      setPendingId(id);
      try {
        const detail = await fetchDetail(id);
        if (!settle(seq)) return;
        onLoadedRef.current?.(detail);
      } catch (err) {
        if (!settle(seq)) return;
        toast.error(
          err?.message ||
            errorMessageRef.current ||
            "Der Datensatz konnte nicht geladen werden."
        );
      }
    },
    [fetchDetail, settle]
  );

  return { requestEdit, pendingId, isLoading: pendingId !== null };
}
